import './styles.scss';

import { faChevronDown, faChevronRight } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useContext, useEffect } from 'react';

import { PNLContext } from '../../../../../../providers/pnl-provider';
import Utils from '../../../../../../utils/immersive_utils';
import { CheckLastUpdate } from '../../../../components/check-last-update';

const { formatNumber } = Utils;
interface IProps {
  data: any[];
  header: any[];
  lastUpdatePNL: string;
  newHeader: any[];
}

export const LegacyPnlTable = (props: IProps) => {
  const { data, header, lastUpdatePNL, newHeader } = props;

  const {
    isFirstLoadData,
    filters: {
      expandableRows,
      expandableColums,
      setExpandableRows,
      setExpandableColums,
      pnlRiskToken,
    },
  } = useContext(PNLContext);

  const handleExpandable = (id: string) => {
    if (!expandableRows.includes(id)) {
      setExpandableRows((prev) => [...prev, id]);
    } else {
      setExpandableRows((prev) => prev.filter((item) => item !== id));
    }
  };

  const renderDataLeft = (block: any[]) => {
    const renderChildrenTableLeft = (arr: any[], level: number) => {
      return arr.map((item, index) => {
        if (!!item.children && item.children.length) {
          return renderNodeItem(item, level, `${item.id}_${index}`);
        } else {
          return (
            <tr className={`level-${level}`} key={`${item.id}_${index}`}>
              <td style={{ width: '1000px' }} className={item.id === 'spot' ? 'spot' : ''}>
                <div
                  className="table-item"
                  style={{
                    paddingLeft: `${16 * level}px`,
                    width: '100%',
                  }}
                >
                  <span className="label first-label">{item.label}</span>
                  <span className="label">{item.position}</span>
                </div>
              </td>
            </tr>
          );
        }
      });
    };

    const countTotalItems = (item: any) => {
      if (!item.children || item.children.length === 0) {
        return 1;
      }

      let totalCount = 0;
      item.children.forEach((child: any) => {
        totalCount += countTotalItems(child);
      });

      return totalCount;
    };

    const renderNodeItem = (item: any, level: number, key: string) => {
      const totalItems = countTotalItems(item);

      return (
        <tr style={{ width: '100%' }} className={`level-${level}`} key={key}>
          <td style={{ paddingBottom: 0, width: '10000px' }}>
            <div className="cell-node">
              <div
                onClick={() => handleExpandable(item.id)}
                className="node-button"
                style={{
                  paddingLeft: `${16 * level}px`,
                }}
              >
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={`icon ${expandableRows.includes(item.id) && 'rotate'}`}
                />
                <span className="label first-label">
                  {item.label}
                  {totalItems > 0 && ` (${totalItems})`}
                </span>
                <span className={`label ${item.position === 'position' ? 'label-position' : ''}`}>
                  {item.position}
                </span>
              </div>
              <div className={`items level-${level}`}>
                {expandableRows.includes(item.id) &&
                  item.children &&
                  renderChildrenTableLeft(item.children, level + 1)}
              </div>
            </div>
          </td>
        </tr>
      );
    };

    return renderChildrenTableLeft(block, 0);
  };

  const renderDataContent = (block: any[]) => {
    const items: any[] = [];

    const dataLoop = (item: any, parent: string[], level: number) => {
      items.push({
        id: item.id,
        label: item.label,
        parent,
        values: item.values,
        level: level,
        hasChildren: !!item.children && item.children.length,
      });

      if (!!item.children && item.children.length) {
        item.children.forEach((child: any) => {
          dataLoop(child, [...parent, item.id], level + 1);
        });
      }
    };

    block.forEach((item) => {
      dataLoop(item, [], 0);
    });

    return items.map((item, index) => {
      let render = true;

      item.parent.forEach((parent: any) => {
        if (!expandableRows.includes(parent)) {
          render = false;
        }
      });

      if (!render) return null;

      return (
        <tr className={`level-${item.level}`} key={`${item.id}__${index}`}>
          {item.values.map((val: any, i: number) => {
            const value = parseFloat(val);

            const canShow =
              newHeader.find((h) => h.label === header[i])?.items !== undefined ||
              expandableColums.find((expandableColumn) =>
                expandableColumn.items.find((item: string) => item === header[i]),
              );

            if (!canShow) {
              return null;
            }

            const formatValueForTable = (value: any, pnlRiskToken: string) => {
              const isNegative = parseFloat(value) < 0;
              const absoluteValue = Math.abs(value);

              const decimalPlaces = pnlRiskToken === 'BASE' ? 6 : 2;

              const formattedValue = formatNumber(absoluteValue, decimalPlaces);
              return isNegative ? `(${formattedValue})` : formattedValue;
            };

            const isChildrenColumn = expandableColums.find((expandableColumn) =>
              expandableColumn.items.find((item: string) => item === header[i]),
            );

            let className = `${isChildrenColumn ? 'children-column' : ''}`;

            if (!isNaN(value) && value < 0) {
              className += ' negative';
            }

            if (isNaN(value)) {
              return (
                <td key={`${val}_${i}`} className={className}>
                  {val}
                </td>
              );
            }

            return (
              <td key={`${val}_${i}`} className={className}>
                {formatValueForTable(value, pnlRiskToken)}
              </td>
            );
          })}
        </tr>
      );
    });
  };

  const renderHeader = (column: any) => {
    const newHeaderColumn = newHeader.find((h) => h.label === column);

    if (newHeaderColumn?.items) {
      const IsShowing = !!expandableColums.find((col) => col.id === newHeaderColumn.id);

      return (
        <th
          key={column}
          style={{ width: '88px' }}
          className="expandable-column"
          onClick={() => {
            setExpandableColums((prev: any) => {
              const prevItem = prev.find((prevItem: any) => prevItem.id === newHeaderColumn.id);

              if (prevItem) {
                return prev.filter((item: any) => prevItem.id !== item.id);
              }

              return [...prev, newHeaderColumn];
            });
          }}
        >
          {column}{' '}
          <FontAwesomeIcon icon={faChevronRight} className={`icon ${IsShowing ? 'rotate' : ''}`} />
        </th>
      );
    } else {
      const canShow =
        newHeader.find((h) => h.label === column)?.items !== undefined ||
        expandableColums.find((expandableColumn) =>
          expandableColumn.items.find((item: string) => item === column),
        );

      if (canShow) {
        return (
          <th key={column} style={{ width: '88px' }}>
            {column}
          </th>
        );
      }
    }
  };

  useEffect(() => {
    if (!isFirstLoadData.current) return;

    if (!data || data.length === 0) return;

    const expandableFirstInstrument = Utils.expandableArrReportsForLevel(data[0][0]);
    const expandableSecondInstrument = Utils.expandableArrReportsForLevel(data[0][1]);

    setExpandableRows([...expandableFirstInstrument, ...expandableSecondInstrument]);
    isFirstLoadData.current = false;
  }, [data]);

  const consoleError = console.error;

  console.error = (err: any) => {
    if (err && (typeof err !== 'string' || !err.includes('cannot appear as a child'))) {
      consoleError(err);
    }
  };

  const hasData = data && data.length > 0 && data[0] && data[0].length > 0;

  return (
    <div className="legacy-pnl-table-container">
      {hasData && <CheckLastUpdate lastUpdate={lastUpdatePNL} />}
      {hasData && (
        <div className="legacy-pnl-table">
          <div className="left-table">
            <table>
              <thead>
                <tr>
                  <th key="instrument">Instrument</th>
                </tr>
              </thead>

              {data.map((block, index) => {
                return <tbody key={`left_block_${index}`}>{renderDataLeft(block)}</tbody>;
              })}
            </table>
          </div>

          <div className="container-content-table">
            <table className="content-table">
              <thead>
                <tr>{header.map((column) => renderHeader(column))}</tr>
              </thead>
              {data.map((block, index) => {
                return <tbody key={`content_block_${index}`}>{renderDataContent(block)}</tbody>;
              })}
            </table>
          </div>
        </div>
      )}

      {!hasData && <div className="empty-body">{`You don't have any positions`}</div>}
    </div>
  );
};
